import {useMemo} from 'react';
import {useJWTAuth} from '../@crema/services/auth/jwt-auth/JWTAuthProvider';

/**
 * Permission constants
 */
export const PERMISSIONS = {
  // Platform Management (Superadmin only)
  PLATFORM_VIEW: 'platform:view',
  PLATFORM_MANAGE_USERS: 'platform:manage_users',
  PLATFORM_MANAGE_PLANS: 'platform:manage_plans',
  PLATFORM_VIEW_ANALYTICS: 'platform:view_analytics',

  // Service Account Management
  SERVICE_VIEW: 'service:view',
  SERVICE_USE: 'service:use',
  SERVICE_CONNECT: 'service:connect',
  SERVICE_SEND: 'service:send',
  SERVICE_BULK: 'service:bulk',
  SERVICE_REPORTS: 'service:reports',

  // Sub-account Management
  SUBACCOUNT_CREATE: 'subaccount:create',
  SUBACCOUNT_MANAGE: 'subaccount:manage',
  SUBACCOUNT_DELETE: 'subaccount:delete',

  // User Management (for account sharing)
  USER_CREATE: 'user:create',
  USER_MANAGE: 'user:manage',
  USER_DELETE: 'user:delete',
};

/**
 * Role constants
 */
export const ROLES = {
  SUPERADMIN: 'superadmin', // Platform owner - full access
  ADMIN: 'admin', // Platform administrator - broad access, below owner (cannot mint superadmins)
  PROVIDER: 'provider', // Can create sub-accounts
  USER: 'user', // End user
  PORTAL_USER: 'portal_user', // Sub-user of a parent, page-scoped permissions
};

// Authorization is EXACT-match against the backend requireRole lists (no implicit
// inheritance); the hierarchy superadmin > admin > provider > user > portal_user is
// expressed by listing every allowed role explicitly in each route/sidebar guard.

/**
 * Parse JWT token
 */
const parseJwt = (token) => {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

/**
 * Custom hook for checking permissions and roles
 */
export const usePermissions = () => {
  const {user, roles: cmsRoles, accessToken} = useJWTAuth();

  // Parse JWT to get WhatsApp-specific claims
  const jwtClaims = useMemo(() => {
    return parseJwt(accessToken);
  }, [accessToken]);

  // Extract role (priority: JWT > user object > CMS role mapping)
  const role = useMemo(() => {
    if (jwtClaims?.whatsappRole) return jwtClaims.whatsappRole;
    if (user?.whatsapp_role) return user.whatsapp_role;
    if (user?.service_role) return user.service_role;

    const cmsRole = user?.role || (cmsRoles?.length > 0 ? cmsRoles[0] : null);
    if (cmsRole === 'superadmin') return ROLES.SUPERADMIN;
    // admin is its OWN tier — never collapse it into superadmin (that mismatched
    // the backend, which distinguishes admin from superadmin via the F3 clamp).
    if (cmsRole === 'admin') return ROLES.ADMIN;
    if (cmsRole === 'provider' || cmsRole === 'manager') return ROLES.PROVIDER;
    if (cmsRole === 'portal_user') return ROLES.PORTAL_USER;

    return ROLES.USER;
  }, [jwtClaims, user, cmsRoles]);

  // Service type (messaging or otp)
  const serviceType = useMemo(() => {
    return jwtClaims?.serviceType || user?.service_type || 'messaging';
  }, [jwtClaims, user]);

  // Get API key
  const apiKey = useMemo(() => {
    return jwtClaims?.apiKey || user?.api_key || null;
  }, [jwtClaims, user]);

  // Is superadmin?
  const isSuperadmin = role === ROLES.SUPERADMIN;

  // Is admin? (platform administrator, below superadmin)
  const isAdmin = role === ROLES.ADMIN;

  // Convenience: platform-management access (owner OR admin), matching the backend
  // requireRole("superadmin","admin") checks.
  const isPlatformManager = isSuperadmin || isAdmin;

  // Is provider?
  const isProvider = role === ROLES.PROVIDER;

  // Is user?
  const isUser = role === ROLES.USER;

  // Is portal user?
  const isPortalUser = role === ROLES.PORTAL_USER;

  // Platform overview data (dashboard stats) — owner + admin per backend
  // requireRole("superadmin","admin") on the dashboard/stats endpoints.
  const canViewPlatformOverview = isPlatformManager;

  // Check if user can manage service users — owner, admin, or provider, matching
  // the backend user-provision guard (superadmin||admin||provider).
  const canManageServiceUsers = isPlatformManager || isProvider;

  // Check if user can set subscription plans (superadmin only)
  const canManagePlans = isSuperadmin;

  // Check if user can delete users (superadmin only)
  const canDeleteUsers = isSuperadmin;

  // Check if user can view their own service overview (everyone)
  const canViewServiceOverview = true;

  // Check if user can use messaging services (everyone)
  const canUseService = true;

  // Check if user can create account-sharing users
  const canCreateAccountUsers = true; // Everyone can create users to share their account

  // Has permission check
  const hasPermission = (permission) => {
    if (!user) return false;
    if (isSuperadmin) return true; // Superadmin has all permissions

    // For provider and user, check specific permissions
    const userPermissions = user?.permissions || [];
    return userPermissions.includes(permission);
  };

  // Has role check
  const hasRole = (requiredRole) => {
    if (Array.isArray(requiredRole)) {
      return requiredRole.includes(role);
    }
    return role === requiredRole;
  };

  // Is authenticated?
  const isAuthenticated = !!user && !!accessToken;

  return {
    // Auth status
    isAuthenticated,

    // Role info
    role,
    isSuperadmin,
    isAdmin,
    isPlatformManager,
    isProvider,
    isUser,
    isPortalUser,

    // Service info
    serviceType,
    apiKey,

    // Permission checks
    hasPermission,
    hasRole,

    // Feature access
    canViewPlatformOverview,
    canViewServiceOverview,
    canManageServiceUsers,
    canManagePlans,
    canDeleteUsers,
    canUseService,
    canCreateAccountUsers,

    // User data
    user,
  };
};

export default usePermissions;
